import type { FeatureConfig } from '~/lib/builder-types';
import { adminPanelTechSpec } from './admin-panel';
import { analyticsTechSpec } from './analytics';
import { apiAccessTechSpec } from './api-access';
import { authTechSpec } from './auth-feature';
import { fileStorageTechSpec } from './file-storage';
import { messagingTechSpec } from './messaging';
import { notificationsTechSpec } from './notifications';
import { paymentsTechSpec } from './payments';
import { searchTechSpec } from './search';
import type {
  FeatureTechSpec,
  FeatureTechSpecDelta,
  TechSpecResolver,
  TechStackConfig,
} from './types';

export { stackPresets } from './stack-presets';
export * from './types';

export const techSpecRegistry: Record<string, TechSpecResolver> = {
  messaging: messagingTechSpec,
  'user-system': authTechSpec,
  notifications: notificationsTechSpec,
  payments: paymentsTechSpec,
  search: searchTechSpec,
  'file-storage': fileStorageTechSpec,
  analytics: analyticsTechSpec,
  'admin-panel': adminPanelTechSpec,
  'api-access': apiAccessTechSpec,
};

export function resolveTechSpec(
  featureSlug: string,
  stack: TechStackConfig,
  featureConfig?: FeatureConfig,
  customizations?: FeatureTechSpecDelta,
): FeatureTechSpec {
  const resolver = techSpecRegistry[featureSlug];
  if (!resolver) return { pages: [], uxFlows: [], endpoints: [], dbTables: [] };
  const base = resolver(stack, featureConfig);
  return applyCustomizations(base, customizations);
}

function applyCustomizations(
  base: FeatureTechSpec,
  delta?: FeatureTechSpecDelta,
): FeatureTechSpec {
  if (!delta) return base;
  return {
    pages: [
      ...base.pages.filter((p) => !delta.pages?.removedIds.includes(p.id)),
      ...(delta.pages?.added ?? []),
    ],
    endpoints: [
      ...base.endpoints.filter(
        (e) => !delta.endpoints?.removedIds.includes(e.id),
      ),
      ...(delta.endpoints?.added ?? []),
    ],
    dbTables: [
      ...base.dbTables.filter(
        (t) => !delta.dbTables?.removedIds.includes(t.id),
      ),
      ...(delta.dbTables?.added ?? []),
    ],
    uxFlows: [
      ...base.uxFlows.filter((f) => !delta.uxFlows?.removedIds.includes(f.id)),
      ...(delta.uxFlows?.added ?? []),
    ],
  };
}
